<template>
  <div data-tour-id="issued-invoices-details-view">
    <!-- LOADER BONITO NO BLOQUEANTE -->
    <div v-if="loading" class="preloader-overlay">
      <div class="preloader-card">
        <f7-preloader />
        <div class="preloader-text">{{ $t('loading') }}</div>
      </div>
    </div>

    <div
      class="tableContainerDetails"
      data-tour-id="issued-invoices-details-summary"
    >
      <div class="card-content padding">
        <f7-row class="card-body">
          <f7-col>
            <div class="label-strong">
              {{ $t('issuedInvoiceCode') }}
            </div>
            <div class="item-title-bold value-text">
              {{ issuedInvoiceDetails.CodFactura }}
            </div>
          </f7-col>
          <f7-col>
            <div class="label-strong">
              {{ $t('issuedInvoiceDate') }}
            </div>
            <div class="item-title-bold value-text">
              {{ formattedIssuedInvoiceDate }}
            </div>
          </f7-col>
          <f7-col v-if="issuedInvoiceDetails.OrdenCompra">
            <div class="label-strong">{{ $t('orderCode') }}</div>
            <div class="item-title-bold value-text">
              {{ issuedInvoiceDetails.OrdenCompra }}
            </div>
          </f7-col>
          <f7-col v-if="getPresupuesto">
            <div class="label-strong">
              {{ $t('samplesRegister.budget') }}
            </div>
            <div class="item-title-bold value-text">
              {{ getPresupuesto }}
            </div>
          </f7-col>
          <f7-col>
            <div class="label-strong">{{ $t('amount') }}</div>
            <div class="item-title-bold value-text">
              {{ formatAmount(issuedInvoiceDetails.ImporteBase) }}
            </div>
          </f7-col>
          <f7-col>
            <div class="label-strong">{{ $t('taxes') }}</div>
            <div class="item-title-bold value-text">
              {{
                formatAmount(issuedInvoiceDetails.ImporteImpuestos)
              }}
            </div>
          </f7-col>
          <f7-col>
            <div class="label-strong">
              {{ $t('totalIncludingTaxes') }}
            </div>
            <div class="item-title-bold value-text">
              {{ formatAmount(issuedInvoiceDetails.ImporteTotal) }}
            </div>
          </f7-col>
          <f7-col>
            <div class="label-strong">
              {{ $t('SamplesList.status') }}
            </div>
            <div
              :class="
                mapBackendStatus(issuedInvoiceDetails.Estado).class
              "
              class="status-text"
            >
              {{
                $t(mapBackendStatus(issuedInvoiceDetails.Estado).i18n)
              }}
            </div>
          </f7-col>
        </f7-row>
        <f7-row v-if="fullComments" class="card-body">
          <f7-col>
            <div class="label-strong">{{ $t('observations') }}</div>
            <div class="item-title-bold value-text">
              {{ fullComments }}
            </div>
          </f7-col>
        </f7-row>
      </div>
    </div>

    <div class="tableContainer no-margin">
      <div class="invoices-header">
        <div class="card-header-invoices">
          {{ $t('issuedInvoiceATList') }}
        </div>

        <div
          class="search-row"
          data-tour-id="issued-invoices-details-search"
        >
          <DxTextBox
            v-model="searchText"
            :placeholder="$t('search')"
            mode="search"
            styling-mode="outlined"
            width="100%"
            value-change-event="input"
          />
        </div>
      </div>

      <div data-tour-id="issued-invoices-at-list">
        <div class="table-header">
          <div></div>
          <div>{{ $t('code') }}</div>
          <div>{{ $t('description') }}</div>
          <div>{{ $t('units') }}</div>
          <div>{{ $t('SamplesList.price') }}</div>
          <div>{{ $t('totalExcludingTaxes') }}</div>
          <div>{{ $t('taxPercentage') }}</div>
        </div>

        <div v-for="(at, index) in filteredATLines" :key="index">
          <div
            class="table-row issued-invoices-at-row"
            :class="{
              'disabled-row': !hasSamplesToShow(at),
            }"
            @click="toggleDetails(index, at)"
          >
            <div class="expand-cell">
              <span
                class="expand-symbol issued-invoices-expand-trigger"
                :class="{ disabled: !hasSamplesToShow(at) }"
              >
                {{ expandedATIndex === index ? '−' : '+' }}
              </span>
            </div>

            <div>{{ at.CodArticulo }}</div>
            <div>{{ at.Articulo }}</div>
            <div>{{ at.Unidades }}</div>
            <div>
              {{
                at.PrecioBase != null
                  ? formatAmount(at.PrecioBase)
                  : '-'
              }}
            </div>
            <div>{{ formatAmount(at.Total) }}</div>
            <div>{{ at.TasaImpuestos }} %</div>
          </div>

          <div
            v-if="
              expandedATIndex === index &&
              filteredSamplesBySearch(at).length
            "
            class="tableContainerSamples"
          >
            <div class="card-header-invoices">
              {{ $t('samples') }}
            </div>

            <div class="table-header-sample">
              <div>{{ $t('code') }}</div>
              <div>{{ $t('description') }}</div>
              <div>{{ $t('date') }}</div>
              <div>{{ $t('samplesRegister.sampleType') }}</div>
              <div></div>
            </div>

            <div
              class="table-row-sample"
              v-for="(muestra, i) in filteredSamplesBySearch(at)"
              :key="i"
            >
              <div>{{ muestra.CodMuestra }}</div>
              <div>{{ muestra.Muestra }}</div>
              <div>{{ muestra.FechaMuestra }}</div>
              <div>{{ muestra.TipoMuestra }}</div>
              <div class="iconVisibilityContainer">
                <img
                  :src="iconVisibility"
                  class="grid-button-icon issued-invoices-sample-view-button"
                  style="cursor: pointer"
                  @click.stop="viewAtc({ row: { data: muestra } })"
                />
              </div>
            </div>
          </div>
        </div>
        <div>
          <small class="subtotal-note">{{
            $t('subtotalNote')
          }}</small>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { NOTIFY_TIME_LONG } from '@/js/constants';
import iconVisibility from '@/static/icons/ic_visibility.svg';
import moment from 'moment';
import { DxTextBox } from 'devextreme-vue/text-box';

export default {
  name: 'IssuedInvoicesDetails',
  components: {
    DxTextBox,
  },
  data() {
    return {
      expandedATIndex: null,
      iconVisibility,
      loading: false,
      searchText: '',
    };
  },

  computed: {
    momentDateFormat() {
      return this.$helpers
        .getDateFormat(this.company)
        .replace('dd', 'DD')
        .replace('yyyy', 'YYYY');
    },
    formattedIssuedInvoiceDate() {
      const d = this.issuedInvoiceDetails?.FechaFacturaDate;
      return d
        ? moment(d).format(this.momentDateFormat)
        : this.issuedInvoiceDetails?.FechaFactura || '';
    },
    fullComments() {
      const baseComment = '';
      const obs = this.getAllObservations();

      if (!obs.length) return baseComment;

      const obsText = `${obs.join('; ')}`;

      return baseComment ? `${baseComment} - ${obsText}` : obsText;
    },
    getPresupuesto() {
      const baseInvoice = '';
      const cods = this.getAllInvoiceCods();

      if (!cods.length) return baseInvoice;

      const codsText = `${cods.join('; ')}`;

      return baseInvoice ? `${baseInvoice} - ${codsText}` : codsText;
    },
    normalizedQuery() {
      return (this.searchText || '').trim().toLowerCase();
    },

    filteredATLines() {
      const lines =
        this.issuedInvoiceDetails?.LineasPorArticulo || [];
      const q = this.normalizedQuery;
      if (!q) return lines;

      return lines.filter((at) => {
        const atText = [
          at.CodArticulo,
          at.Articulo,
          String(at.Unidades ?? ''),
          String(at.PrecioBase ?? ''),
          String(at.Total ?? ''),
          String(at.TasaImpuestos ?? '-'),
          at.Observaciones,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (atText.includes(q)) return true;

        const muestras = this.filteredSamples(at);
        return muestras.some((m) => {
          const mText = [
            m.CodMuestra,
            m.Muestra,
            m.FechaMuestra,
            m.TipoMuestra,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          return mText.includes(q);
        });
      });
    },
    ...mapState('contact', ['company']),
    ...mapState('issuedInvoicesModule', [
      'issuedInvoiceDetails',
      'currency',
    ]),
    ...mapState('samples', ['currentSample']),
    ...mapState('filter', ['currentSampleId']),
  },
  watch: {
    searchText() {
      const list = this.filteredATLines;
      const current = list[this.expandedATIndex];
      if (!current || !this.filteredSamplesBySearch(current).length) {
        this.expandedATIndex = null;
      }
    },
  },
  methods: {
    filteredSamples(at) {
      return at.Muestras?.filter((m) => m.CodMuestra) || [];
    },
    filteredSamplesBySearch(at) {
      const samples = this.filteredSamples(at);
      const q = this.normalizedQuery;
      if (!q) return samples;

      return samples.filter((m) => {
        const mText = [
          m.CodMuestra,
          m.Muestra,
          m.FechaMuestra,
          m.TipoMuestra,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return mText.includes(q);
      });
    },

    hasSamplesToShow(at) {
      return this.filteredSamplesBySearch(at).length > 0;
    },
    getAllObservations() {
      const lines = this.issuedInvoiceDetails?.LineasPorArticulo;
      if (!Array.isArray(lines)) return [];

      const obs = [];

      lines.forEach((at) => {
        if (at.Observaciones && !obs.includes(at.Observaciones)) {
          obs.push(at.Observaciones);
        }
      });

      return obs;
    },
    getAllInvoiceCods() {
      const lines = this.issuedInvoiceDetails?.LineasPorArticulo;
      if (!Array.isArray(lines)) return [];

      const cods = [];

      lines.forEach((at) => {
        if (at.CodPresupuesto && !cods.includes(at.CodPresupuesto)) {
          cods.push(at.CodPresupuesto);
        }
      });

      return cods;
    },
    formatAmount(amount) {
      if (!amount && amount !== 0) return '';
      const parsedAmount = parseFloat(
        typeof amount === 'string'
          ? amount.replace(',', '.').replace(/[^\d.]/g, '')
          : amount
      );
      return new Intl.NumberFormat(this.$i18n.locale || 'es', {
        style: 'currency',
        currency: this.currency || 'EUR',
        minimumFractionDigits: 2,
      }).format(parsedAmount);
    },
    mapBackendStatus(value) {
      const map = {
        Pendiente: {
          class: 'orangeStatus',
          i18n: 'issuedInvoiceStatus.pending',
        },
        Pagada: {
          class: 'greenStatus',
          i18n: 'issuedInvoiceStatus.paid',
        },
        OK: {
          class: 'greyStatus',
          i18n: 'issuedInvoiceStatus.ok',
        },
        '': {
          class: 'greyStatus',
          i18n: 'issuedInvoiceStatus.ok',
        },
      };

      return (
        map[value] || {
          class: 'greyStatus',
          i18n: value,
        }
      );
    },
    toggleDetails(index, at) {
      if (!this.hasSamplesToShow(at)) return;
      this.expandedATIndex =
        this.expandedATIndex === index ? null : index;
    },
    hasTourSampleAction() {
      return this.getFirstTourExpandableLineIndex() !== -1;
    },
    getFirstTourExpandableLineIndex() {
      return this.filteredATLines.findIndex((at) =>
        this.hasSamplesToShow(at)
      );
    },
    async prepareTourSampleAction() {
      this.searchText = '';
      await this.$nextTick();

      const firstExpandableLineIndex =
        this.getFirstTourExpandableLineIndex();

      if (firstExpandableLineIndex === -1) {
        this.expandedATIndex = null;
        return false;
      }

      this.expandedATIndex = firstExpandableLineIndex;
      await this.$nextTick();
      return true;
    },
    async viewAtc(e) {
      try {
        this.loading = true;
        this.setSampleFetchedFromEtl(false);
        this.setLastRouteHome(false);
        this.setLastRouteGeotoolboxSamples(false);
        this.setLastRouteIssuedInvoicesList(true);

        this.setReportSampleCode(e.row.data.CodMuestra);

        const currentSample = await this.getCurrentSampleByCode();

        this.setCurrentSampleId(currentSample.id);

        await this.goToSampleDetailsByAtc(
          currentSample?.atc_name,
          currentSample
        );
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                JSON.parse(error.message)?.error || error.message
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.loading = false;
      }
    },
    async downloadInvoice() {
      const codFactura = this.issuedInvoiceDetails?.CodFactura;
      if (!codFactura) return;

      try {
        this.loading = true;
        const b64 = await this.fetchIssuedInvoicePDF(codFactura);
        const clean = b64.includes(',') ? b64.split(',')[1] : b64;

        const byteChars = atob(clean);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i += 1) {
          byteNumbers[i] = byteChars.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: 'application/pdf',
        });

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${codFactura}.pdf`;
        a.classList.add('external');
        a.rel = 'noopener external';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$notifyDX?.(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage?.(
                error?.message
              ) || 'downloadError'
            ),
            width: 550,
          },
          'error'
        );
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    async goToSampleDetailsByAtc(atcName, sample) {
      this.types = {
        food: this.$t('Food'),
        agronomy: this.$t('Agronomy'),
        environment: this.$t('Environment'),
        mining: this.$t('Mining'),
        healthAndSecurity: this.$t('Health and Security'),
      };

      if (atcName === this.types.food) {
        this.setCurrentFilter('foodDetails');
        this.setIsNoRp(sample.value_report === 'CMA');
        this.setSelectedLimits([this.$t('positives')]);

        if (sample.value_report !== 'CMA') {
          if (sample.retailers) {
            const chains = [...sample.retailers];
            const res = [];
            chains.forEach((chainData) => {
              chainData.Reglas.forEach((chain) => {
                if (
                  res.length > 0 &&
                  res.some(
                    (el) => el.Supermercado === chainData.Supermercado
                  )
                ) {
                  const indexRes = res.findIndex(
                    (el) => el.Supermercado === chainData.Supermercado
                  );
                  res[indexRes] = {
                    ...res[indexRes],
                    [`Regla ${chain.Regla}`]: chain.Regla,
                    [`Resultado ${chain.Regla}`]: chain.Resultado,
                    [`CodRegla ${chain.Regla}`]: chain.CodRegla,
                    Supermercado: chainData.Supermercado,
                    Confirmado: chainData.Confirmado,
                  };
                } else {
                  res.push({
                    [`Resultado ${chain.Regla}`]: chain.Resultado,
                    [`Regla ${chain.Regla}`]: chain.Regla,
                    [`CodRegla ${chain.Regla}`]: chain.CodRegla,
                    Supermercado: chainData.Supermercado,
                    Confirmado: chainData.Confirmado,
                    ...chain,
                  });
                }
              });
            });
            this.setEuropeanChains(res);
          } else {
            this.setEuropeanChains([]);
          }
        }

        this.pushRoute({
          name: this.$t('Food'),
          disabled: false,
          page: 'FoodSamplesPage',
          active: false,
        });
        this.pushRoute({
          name: this.$t('sampleSummary'),
          disabled: true,
          page: 'FoodSampleDetailPage',
          active: true,
        });
        this.$f7router.navigate(
          {
            name: 'FoodSampleDetailPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } else if (atcName === this.types.agronomy) {
        this.setCurrentFilter('agronomy');
        this.pushRoute({
          name: this.$t('Agronomy'),
          disabled: false,
          page: 'AgronomySamplesPage',
          active: false,
        });
        this.pushRoute({
          name: this.$t('sampleSummary'),
          disabled: true,
          page: 'AgronomySamplesDetailsPage',
          active: true,
        });
        this.$f7router.navigate(
          {
            name: 'AgronomySamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } else if (atcName === this.types.environment) {
        this.setCurrentFilter('environmentDetails');
        this.pushRoute({
          name: this.$t('Environment'),
          disabled: false,
          page: 'EnvironmentSamplesPage',
          active: false,
        });
        this.pushRoute({
          name: this.$t('sampleSummary'),
          disabled: true,
          page: 'EnvironmentSamplesDetailsPage',
          active: true,
        });
        this.$f7router.navigate(
          {
            name: 'EnvironmentSamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } else if (atcName === this.types.mining) {
        this.setCurrentFilter('miningDetails');
        this.pushRoute({
          name: this.$t('Mining'),
          disabled: false,
          page: 'MiningSamplesPage',
          active: false,
        });
        this.pushRoute({
          name: this.$t('sampleSummary'),
          disabled: true,
          page: 'MiningSamplesDetailsPage',
          active: true,
        });
        this.$f7router.navigate(
          {
            name: 'MiningSamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      } else if (atcName === this.types.healthAndSecurity) {
        this.setCurrentFilter('healthSecurityDetails');
        this.pushRoute({
          name: this.$t('Health and Security'),
          disabled: false,
          page: 'HealthSecuritySamplesPage',
          active: false,
        });
        this.pushRoute({
          name: this.$t('sampleSummary'),
          disabled: true,
          page: 'HealthSecuritySamplesDetailsPage',
          active: true,
        });
        this.$f7router.navigate(
          {
            name: 'HealthSecuritySamplesDetailsPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      }
    },
    ...mapActions('filter', [
      'setCurrentFilter',
      'setCurrentSampleId',
    ]),
    ...mapActions('samples', [
      'getReport',
      'setReportSampleId',
      'getCurrentSampleByCode',
      'setReportSampleCode',
      'setDisableContext',
      'getSamplesWithParameters',
      'setAllFilterParams',
      'getSamplesZip',
      'setIsDownloadingReport',
      'setSampleFetchedFromEtl',
      'setLastRouteHome',
      'setLastRouteGeotoolboxSamples',
      'setLastRouteIssuedInvoicesList',
    ]),
    ...mapActions('homeSamples', ['setSamples']),
    ...mapActions('foodSamples', ['setIsNoRp', 'setEuropeanChains']),
    ...mapActions('foodDetailsFilter', ['setSelectedLimits']),
    ...mapActions('breadcrumb', ['pushRoute']),
    ...mapActions('issuedInvoicesModule', ['fetchIssuedInvoicePDF']),
  },
};
</script>

<style
  scoped
  lang="sass"
  src="./issued-invoices-details.scss"
></style>
